// AfasTheme.ts
import { createTheme } from "@mui/material/styles";

// ---- Palette augmentation (adds dark & lightAccent to the palette) ----
declare module "@mui/material/styles" {
  interface Palette {
    dark: Palette["primary"];
    lightAccent: Palette["primary"];
  }
  interface PaletteOptions {
    dark?: PaletteOptions["primary"];
    lightAccent?: PaletteOptions["primary"];
  }
}

const baseTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 520,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

const AfasTheme = createTheme(baseTheme, {
  typography: {
    fontFamily: ['"Roboto"', '"Segoe UI"', '"Helvetica Neue"', "Arial", "sans-serif"].join(","),
    h1: {
      fontWeight: 700,
      fontSize: "clamp(2rem, 1.2857rem + 2.2857vw, 3rem)",
      lineHeight: 1,
      textTransform: "none",
    },
    h2: { fontWeight: 700, fontSize: "28px", lineHeight: 1.2, textTransform: "none" },
    h3: { fontWeight: 600, fontSize: "22px", lineHeight: 1.25 },
    h4: { fontWeight: 600, fontSize: "18px" },
    h5: { fontWeight: 600, fontSize: "16px" },
    body1: { fontWeight: 300, fontSize: "18px", lineHeight: "normal" },
  },
  palette: {
    primary: { main: "#0071BC", dark: "#005B98", contrastText: "#ffffff" },
    secondary: { main: "#F59E0B", contrastText: "#ffffff" },
    warning: { main: "#F59E0B", contrastText: "#FFFFFF" },
    error: { main: "#F59E0B", contrastText: "#FFFFFF" },
    dark: { light: "rgba(65, 71, 77, .4)", main: "#41474D", contrastText: "#ffffff" },
    action: { selected: "#FFFBEB", hover: "#FFFBEB" },
    lightAccent: { main: "#F2F5F8" },
  },
  components: {
    MuiTypography: {
      variants: [
        { props: { variant: "h1" }, style: { marginBottom: "0.25rem" } },
        { props: { variant: "h2" }, style: { marginBottom: "0.5rem" } },
        { props: { variant: "h3" }, style: { marginBottom: "0.75rem" } },
        { props: { variant: "body1" }, style: { marginBottom: "0.75rem" } },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "5px",
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: "18px",
          textTransform: "none",
          padding: "1rem 1.5rem",
          boxShadow: "none",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "0px",
          border: "unset",
          boxShadow: "none",
          padding: "0",
          backgroundColor: "#ffffff",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: "5px",
          backgroundColor: "#ffffff",
          boxShadow: ": 1px solid rgba(0, 95, 170, 0.20)",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& fieldset": {
            borderColor: "#F29F05",
          },
          "& input::placeholder": {
            color: "rgba(65, 71, 77, .4) !important",
            opacity: 1,
            fontSize: "14px",
          },
          "&:hover fieldset": {
            borderColor: "#e38f00",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#e38f00",
            borderWidth: 2,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 95, 170, 0.20);",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(0, 95, 170, 0.20);",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0071BC",
            borderWidth: "1px",
            borderLeftWidth: "5px",
          },
        },
        input: { padding: "8px 16px", borderRadius: "5px", minHeight: "50px", boxSizing: "border-box" },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "#374151",
          fontWeight: 600,
          marginBottom: "4px",
          "& .MuiFormLabel-asterisk": { color: "#FFA000" },
        },
      },
    },
    MuiAlert: { styleOverrides: { root: { borderRadius: 6 } } },
    MuiTableCell: { styleOverrides: { root: { paddingLeft: 8, paddingRight: 8 } } },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "0px",
          border: "1px solid rgba(0, 120, 215, 0.10)",
          boxShadow: "none",
          backgroundColor: "#ffffff",
          "&.AfasBluePanel": {
            backgroundColor: "#0071BC",
            color: "#ffffff",
            borderRadius: "0px",
            padding: "1.25rem",
            [baseTheme.breakpoints.up("sm")]: {
              padding: "1.75rem 2.25rem",
            },
            [baseTheme.breakpoints.up("md")]: {
              padding: "2.125rem 2.75rem",
            },
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundImage: "url('/images/afasbg.png')",
          },
        },
      },
    },
  },
});

export default AfasTheme;
